import React, { useCallback, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import LikeIcon from '@atlaskit/icon/core/thumbs-up';
import DislikeIcon from '@atlaskit/icon/core/thumbs-down';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { IconButton } from '@atlaskit/button/new';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import RetryIcon from '@atlaskit/icon/core/retry';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { useRegenerateLoadingState } from '../../useRegenerateLoadingState.ts';

import { FollowUpQuestions } from './FollowUpQuestions.tsx';
import { RovoInput } from './RovoInput.tsx';
import { SourcesSection } from './SourcesSection.tsx';
import { useFAQDwellEvent } from './useFAQDwellEvent.ts';
import type { FAQData } from './PopularQuestions.tsx';

type FAQEntry = NonNullable<FAQData['entries']>[number];

type QuestionItemProps = {
	question: FAQEntry;
	questionPosition: number;
	spaceKey: string;
	isExpanded: boolean;
	onToggle: (id: string) => void;
	onFollowUp?: (followUpQuestion: string, parentQuestion: FAQEntry) => void;
	onAskRovo?: (customQuestion: string, parentQuestion: FAQEntry) => void;
};

type FeedbackButtonsProps = {
	vote: VoteDirection;
	onVoteUp: () => void;
	onVoteDown: () => void;
	upLabel: string;
	downLabel: string;
};

type VoteDirection = 'up' | 'down' | null;

type RefreshButtonProps = {
	label: string;
	isLoading: boolean;
	onClick: (e: React.MouseEvent) => void | Promise<void>;
};

const FeedbackButtons = ({
	vote,
	onVoteUp,
	onVoteDown,
	upLabel,
	downLabel,
}: FeedbackButtonsProps) => (
	<>
		<IconButton
			icon={() => (
				<LikeIcon
					label=""
					color={vote === 'up' ? token('color.icon.accent.blue') : token('color.icon.subtlest')}
				/>
			)}
			label={upLabel}
			appearance="subtle"
			spacing="compact"
			shape="default"
			onClick={(e) => {
				e.stopPropagation();
				if (vote !== 'up') onVoteUp();
			}}
			isTooltipDisabled={false}
			isSelected={vote === 'up'}
		/>
		<IconButton
			icon={() => (
				<DislikeIcon
					label=""
					color={vote === 'down' ? token('color.icon.accent.blue') : token('color.icon.subtlest')}
				/>
			)}
			label={downLabel}
			appearance="subtle"
			spacing="compact"
			shape="default"
			onClick={(e) => {
				e.stopPropagation();
				if (vote !== 'down') onVoteDown();
			}}
			isTooltipDisabled={false}
			isSelected={vote === 'down'}
		/>
	</>
);

const RefreshButton = ({ label, isLoading, onClick }: RefreshButtonProps) => (
	<IconButton
		icon={RetryIcon}
		label={label}
		appearance="subtle"
		spacing="compact"
		isLoading={isLoading}
		onClick={onClick}
		isTooltipDisabled={false}
	/>
);

const QuestionHeader = ({
	questionText,
	isExpanded,
	onToggle,
	isRefreshing,
	onRefresh,
	questionVote,
	onVoteUp,
	onVoteDown,
}: {
	questionText: string;
	isExpanded: boolean;
	onToggle: () => void;
	isRefreshing: boolean;
	onRefresh: (e: React.MouseEvent) => void;
	questionVote: VoteDirection;
	onVoteUp: () => void;
	onVoteDown: () => void;
}) => {
	const { formatMessage } = useIntl();

	const handleQuestionRefresh = (e: React.MouseEvent) => {
		e.stopPropagation();
		onRefresh(e);
	};

	return (
		<div
			css={[
				styles.questionRow,
				isExpanded ? styles.questionRowExpanded : styles.questionRowCollapsed,
			]}
		>
			<Pressable
				xcss={styles.questionTrigger}
				onClick={onToggle}
				aria-expanded={isExpanded}
				aria-label={formatMessage(isExpanded ? i18n.collapseQuestion : i18n.expandQuestion, {
					question: questionText,
				})}
			/>
			<div css={styles.questionIcon}>
				<RovoChatIcon label="" color={token('color.icon.subtle')} />
			</div>
			<div css={styles.questionText}>
				<Text size="medium" color="color.text.subtle">
					{questionText}
				</Text>
			</div>
			<div
				css={[
					styles.questionFeedbackButtons,
					!fg('smart-space-overview-faq-always-show-feedback') && styles.hideQuestionActions,
				]}
			>
				{fg('smart-space-overview-individual-faq-regeneration') && (
					<RefreshButton
						label={formatMessage(i18n.refreshQuestion)}
						isLoading={isRefreshing}
						onClick={handleQuestionRefresh}
					/>
				)}
				<FeedbackButtons
					vote={questionVote}
					onVoteUp={onVoteUp}
					onVoteDown={onVoteDown}
					upLabel={formatMessage(i18n.thumbsUpQuestion)}
					downLabel={formatMessage(i18n.thumbsDownQuestion)}
				/>
			</div>
			<div css={styles.chevron}>
				{isExpanded ? (
					<ChevronUpIcon
						label={formatMessage(i18n.collapseQuestion, { question: questionText })}
						color={token('color.icon.subtle')}
					/>
				) : (
					<ChevronDownIcon
						label={formatMessage(i18n.expandQuestion, { question: questionText })}
						color={token('color.icon.subtle')}
					/>
				)}
			</div>
		</div>
	);
};

const QuestionItemInner = ({
	question,
	questionPosition,
	spaceKey,
	isExpanded,
	onToggle,
	onFollowUp,
	onAskRovo,
}: QuestionItemProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();
	const [questionVote, setQuestionVote] = useState<VoteDirection>(null);
	const [answerVote, setAnswerVote] = useState<VoteDirection>(null);
	const [startQuestionRefresh, isQuestionRefreshing] = useRegenerateLoadingState();
	const [startAnswerRefresh, isAnswerRefreshing] = useRegenerateLoadingState();
	const expandedContentRef = useRef<HTMLDivElement>(null);
	const answerTextRef = useRef<HTMLDivElement>(null);

	const { markManualCollapse } = useFAQDwellEvent({
		isExpanded,
		expandedContentRef: answerTextRef,
		spaceKey,
		questionPosition,
	});

	const showFeedbackFlag = useCallback(() => {
		showFlag({
			title: formatMessage(i18n.feedbackFlagTitle),
			description: formatMessage(i18n.feedbackFlagDescription),
			icon: <StatusSuccessIcon color={token('color.icon.success')} label="success" />,
			isAutoDismiss: true,
		});
	}, [showFlag, formatMessage]);

	const handleToggle = useCallback(() => {
		const dwellMetrics = markManualCollapse();
		onToggle(question.question);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: isExpanded ? 'questionCollapse' : 'questionExpand',
				source: 'smartSpaceOverview',
				attributes: {
					moduleType: 'faq',
					spaceKey,
					questionPosition,
					...(dwellMetrics ?? {}),
				},
			},
		}).fire();
	}, [
		onToggle,
		question.question,
		isExpanded,
		createAnalyticsEvent,
		questionPosition,
		spaceKey,
		markManualCollapse,
	]);

	const handleFollowUp = useCallback(
		(followUpQuestion: string, followUpPosition: number) => {
			onFollowUp?.(followUpQuestion, question);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'followUpQuestion',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'faq',
						spaceKey,
						questionPosition,
						followUpPosition,
					},
				},
			}).fire();
		},
		[onFollowUp, question, createAnalyticsEvent, questionPosition, spaceKey],
	);

	const handleAskRovo = useCallback(
		(message: string) => {
			onAskRovo?.(message, question);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'askRovoSubmit',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'faq',
						spaceKey,
						questionPosition,
						queryLength: message.length,
					},
				},
			}).fire();
		},
		[onAskRovo, question, createAnalyticsEvent, questionPosition, spaceKey],
	);

	const fireFeedbackEvent = useCallback(
		(actionSubjectId: 'question' | 'answer', wasHelpful: boolean) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'submitted',
					actionSubject: 'feedback',
					actionSubjectId,
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'faq',
						spaceKey,
						questionPosition,
						wasHelpful,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, questionPosition, spaceKey],
	);

	const handleQuestionThumbsUp = useCallback(() => {
		setQuestionVote('up');
		showFeedbackFlag();
		fireFeedbackEvent('question', true);
	}, [showFeedbackFlag, fireFeedbackEvent]);

	const handleQuestionThumbsDown = useCallback(() => {
		setQuestionVote('down');
		showFeedbackFlag();
		fireFeedbackEvent('question', false);
	}, [showFeedbackFlag, fireFeedbackEvent]);

	const handleAnswerThumbsUp = useCallback(() => {
		setAnswerVote('up');
		showFeedbackFlag();
		fireFeedbackEvent('answer', true);
	}, [showFeedbackFlag, fireFeedbackEvent]);

	const handleAnswerThumbsDown = useCallback(() => {
		setAnswerVote('down');
		showFeedbackFlag();
		fireFeedbackEvent('answer', false);
	}, [showFeedbackFlag, fireFeedbackEvent]);

	const handleQuestionRefresh = (e: React.MouseEvent) => {
		e.stopPropagation();
		setQuestionVote(null);
		setAnswerVote(null);
		startQuestionRefresh();
	};

	const handleAnswerRefresh = (_e: React.MouseEvent) => {
		setAnswerVote(null);
		startAnswerRefresh();
	};

	return (
		<div css={[styles.cardWrapper, isQuestionRefreshing && styles.contentBlurred]}>
			<QuestionHeader
				questionText={question.question}
				isExpanded={isExpanded}
				onToggle={handleToggle}
				isRefreshing={isQuestionRefreshing}
				onRefresh={handleQuestionRefresh}
				questionVote={questionVote}
				onVoteUp={handleQuestionThumbsUp}
				onVoteDown={handleQuestionThumbsDown}
			/>

			{isExpanded && (
				<div
					ref={expandedContentRef}
					css={[styles.expandedContent, isAnswerRefreshing && styles.contentBlurred]}
				>
					<Stack space="space.200">
						<Stack space="space.100">
							<div css={styles.sectionLabel}>
								<Text size="small" weight="medium" color="color.text.subtle">
									{formatMessage(i18n.answerLabel)}
								</Text>
								<div css={styles.answerActions}>
									<div
										css={[
											styles.answerFeedbackButtons,
											!fg('smart-space-overview-faq-always-show-feedback') &&
												styles.hideAnswerActions,
										]}
									>
										{fg('smart-space-overview-individual-faq-regeneration') && (
											<RefreshButton
												label={formatMessage(i18n.refreshAnswer)}
												isLoading={isAnswerRefreshing}
												onClick={handleAnswerRefresh}
											/>
										)}
										<FeedbackButtons
											vote={answerVote}
											onVoteUp={handleAnswerThumbsUp}
											onVoteDown={handleAnswerThumbsDown}
											upLabel={formatMessage(i18n.thumbsUpAnswer)}
											downLabel={formatMessage(i18n.thumbsDownAnswer)}
										/>
									</div>
									<SourcesSection
										sources={question.sources}
										spaceKey={spaceKey}
										questionPosition={questionPosition}
									/>
								</div>
							</div>
							<div ref={answerTextRef}>
								<Text size="medium" color="color.text.subtle">
									{question.answer}
								</Text>
							</div>
						</Stack>

						<FollowUpQuestions questions={question.followUpQuestions} onFollowUp={handleFollowUp} />

						<RovoInput onSend={handleAskRovo} />
					</Stack>
				</div>
			)}
		</div>
	);
};

export const QuestionItem = (props: QuestionItemProps) => (
	<FlagsProvider>
		<QuestionItemInner {...props} />
	</FlagsProvider>
);

const i18n = defineMessages({
	expandQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.expand',
		defaultMessage: 'Expand question: {question}',
		description: 'Accessible label for the button to expand a popular question.',
	},
	collapseQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.collapse',
		defaultMessage: 'Collapse question: {question}',
		description: 'Accessible label for the button to collapse a popular question.',
	},
	answerLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.answer-label',
		defaultMessage: 'Answer',
		description: 'Label for the answer section of a popular question.',
	},
	thumbsUpQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-up',
		defaultMessage: 'Good question',
		description: 'Label for the question thumbs up button.',
	},
	thumbsDownQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-down',
		defaultMessage: 'Bad question',
		description: 'Label for the question thumbs down button.',
	},
	thumbsUpAnswer: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-up-answer',
		defaultMessage: 'Good answer',
		description:
			'The text is shown as an accessible label on the thumbs-up button displayed next to an AI-generated answer in the popular questions section of the smart space overview. Clicking it signals that the answer was helpful.',
	},
	thumbsDownAnswer: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-down-answer',
		defaultMessage: 'Bad answer',
		description: 'Label for the answer thumbs down button.',
	},
	refreshQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.refresh-question',
		defaultMessage: 'Refresh question',
		description:
			'The text is shown as an accessible label on the refresh button displayed next to a question in the popular questions section of the smart space overview. Clicking it generates a new AI-powered question to replace the current one.',
	},
	refreshAnswer: {
		id: 'space-overview.smart-space-overview.popular-questions.refresh-answer',
		defaultMessage: 'Refresh answer',
		description:
			'The text is shown as an accessible label on the refresh button displayed next to an AI-generated answer in the popular questions section of the smart space overview. Clicking it regenerates the AI answer for the current question.',
	},
	feedbackFlagTitle: {
		id: 'space-overview.smart-space-overview.popular-questions.feedback-flag-title',
		defaultMessage: 'Thanks!',
		description:
			'Title of the success flag shown after a user submits feedback on a question or answer.',
	},
	feedbackFlagDescription: {
		id: 'space-overview.smart-space-overview.popular-questions.feedback-flag-description',
		defaultMessage: 'We appreciate you taking the time to send this feedback.',
		description:
			'Description of the success flag shown after a user submits feedback on a question or answer.',
	},
});

const styles = cssMap({
	cardWrapper: {
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		overflow: 'hidden',
	},
	questionRow: {
		position: 'relative',
		display: 'grid',
		gridTemplateColumns: 'auto 1fr auto auto',
		gridTemplateAreas: '"icon text feedback chevron"',
		alignItems: 'center',
		columnGap: token('space.025'),
		cursor: 'pointer',
		backgroundColor: token('elevation.surface.sunken'),
		borderWidth: '0',
		width: '100%',
		font: token('font.body'),
		color: token('color.text'),
		textAlign: 'start',
		'--question-feedback-opacity': '0',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			// @ts-expect-error -- CSS custom property not supported by cssMap types
			'--question-feedback-opacity': '1',
		},
	},
	questionRowExpanded: {
		borderRadius: '0',
	},
	questionRowCollapsed: {
		borderRadius: token('radius.xlarge'),
	},
	questionTrigger: {
		position: 'absolute',
		inset: token('space.0'),
		backgroundColor: 'transparent',
	},
	questionIcon: {
		gridArea: 'icon',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		paddingInlineStart: token('space.250'),
		pointerEvents: 'none',
	},
	questionText: {
		gridArea: 'text',
		position: 'relative',
		minWidth: '0px',
		paddingInline: token('space.100'),
		paddingBlock: token('space.200'),
		pointerEvents: 'none',
	},
	questionFeedbackButtons: {
		gridArea: 'feedback',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		flexShrink: 0,
	},
	chevron: {
		gridArea: 'chevron',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		paddingInlineEnd: token('space.250'),
		pointerEvents: 'none',
	},
	expandedContent: {
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.250'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		'--answer-feedback-opacity': '0',
		'&:hover': {
			// @ts-expect-error -- CSS custom property not supported by cssMap types
			'--answer-feedback-opacity': '1',
		},
	},
	sectionLabel: {
		paddingBlockEnd: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	answerActions: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexWrap: 'nowrap',
		columnGap: token('space.050'),
		flexShrink: 0,
	},
	answerFeedbackButtons: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		flexShrink: 0,
	},
	hideQuestionActions: {
		transition: 'opacity 150ms ease-in',
		// @ts-expect-error -- CSS custom property not supported by cssMap types
		opacity: 'var(--question-feedback-opacity)',
	},
	hideAnswerActions: {
		transition: 'opacity 150ms ease-in',
		// @ts-expect-error -- CSS custom property not supported by cssMap types
		opacity: 'var(--answer-feedback-opacity)',
	},
	contentBlurred: {
		opacity: token('opacity.disabled'),
		transition: 'opacity 150ms ease-in',
		pointerEvents: 'none',
	},
});
